import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../hooks/useAuth.jsx";
import api from "../api/index.js";
import SEO from "../components/SEO.jsx";

const G = {
  bg:"#0A0A0A", surface:"#111111", border:"#1E1E1E",
  text:"#E4E4DC", muted:"#666", accent:"#00C896",
  purple:"#7C6FFF", danger:"#FF6B6B", warn:"#FFB347",
  font:"'Sora', sans-serif", mono:"'DM Mono', monospace",
};

const StatCard = ({ label, value, sub, color=G.accent, icon }) => (
  <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:"20px 24px" }}>
    <div style={{ fontSize:24, marginBottom:8 }}>{icon}</div>
    <div style={{ fontSize:32, fontWeight:800, color, fontFamily:G.mono }}>{value}</div>
    {sub && <div style={{ fontSize:12, color:G.accent, fontFamily:G.mono, marginTop:2 }}>{sub}</div>}
    <div style={{ fontSize:12, color:G.muted, marginTop:4 }}>{label}</div>
  </div>
);

const MiniBar = ({ value, max, color=G.accent }) => (
  <div style={{ background:"#1A1A1A", borderRadius:4, height:6, flex:1 }}>
    <div style={{ background:color, height:"100%", borderRadius:4, width:`${max>0?(value/max)*100:0}%`, transition:"width 0.3s" }} />
  </div>
);

export default function AnalyticsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData]     = useState(null);
  const [loading, setLoading] = useState(true);
  const [tab, setTab]       = useState("overview");

  useEffect(() => {
    if (!user) return navigate("/login");
    if (!user.is_admin) return navigate("/");
    api.get("/admin/analytics")
      .then(r => setData(r.data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [user, navigate]);

  if (loading) return <div style={{ textAlign:"center", padding:"80px 0", color:G.muted, fontFamily:G.font }}>Loading analytics...</div>;
  if (!data) return <div style={{ textAlign:"center", padding:"80px 0", color:G.danger, fontFamily:G.font }}>Failed to load analytics</div>;

  const maxViews = Math.max(...(data.topQuestions.map(q => q.views) || [1]));
  const maxActivity = Math.max(...(data.topUsers.map(u => u.total_activity) || [1]));
  const maxQCount = Math.max(...(data.topTopics.map(t => t.question_count) || [1]));

  // Build simple chart data from daily stats
  const last14days = Array.from({length:14}, (_, i) => {
    const d = new Date();
    d.setDate(d.getDate() - (13 - i));
    const dateStr = d.toISOString().split("T")[0];
    const qDay = data.dailyQuestions.find(x => x.day?.startsWith(dateStr));
    const aDay = data.dailyAnswers.find(x => x.day?.startsWith(dateStr));
    return { date: dateStr, shortDate: d.toLocaleDateString("en-US",{month:"short",day:"numeric"}), questions: qDay?.count||0, answers: aDay?.count||0 };
  });
  const maxBar = Math.max(...last14days.map(d => Math.max(d.questions, d.answers)), 1);

  return (
    <div style={{ maxWidth:1100, margin:"0 auto", padding:"28px 16px", fontFamily:G.font }}>
      <SEO title="Analytics" noindex />

      {/* Header */}
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom:28 }}>
        <div>
          <div style={{ fontSize:11, color:G.accent, fontFamily:G.mono, letterSpacing:2, textTransform:"uppercase", marginBottom:4 }}>Admin</div>
          <h1 style={{ fontSize:28, fontWeight:700, color:G.text, margin:0 }}>Analytics</h1>
        </div>
        <div style={{ display:"flex", gap:10 }}>
          <Link to="/admin" style={{ textDecoration:"none", color:G.muted, fontSize:13, fontFamily:G.mono }}>← Dashboard</Link>
        </div>
      </div>

      {/* Tabs */}
      <div style={{ display:"flex", gap:4, marginBottom:24, background:G.surface, border:`1px solid ${G.border}`, borderRadius:10, padding:4, width:"fit-content" }}>
        {[["overview","Overview"],["questions","Questions"],["users","Users"],["listings","Listings"]].map(([t,l]) => (
          <button key={t} onClick={() => setTab(t)} style={{
            background:tab===t?"#1A1A1A":"transparent",
            border:tab===t?`1px solid #2A2A2A`:"1px solid transparent",
            color:tab===t?G.accent:G.muted,
            padding:"6px 18px", borderRadius:7, cursor:"pointer",
            fontSize:13, fontFamily:G.mono, transition:"all 0.15s"
          }}>{l}</button>
        ))}
      </div>

      {/* Overview Tab */}
      {tab === "overview" && (
        <div>
          {/* Growth Stats */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:16, marginBottom:28 }}>
            <StatCard label="Total Users" value={data.growth.total_users} sub={`+${data.growth.new_users_7d} this week`} color={G.accent} icon="👥" />
            <StatCard label="New Users (7d)" value={data.growth.new_users_7d} sub={`+${data.growth.new_users_30d} this month`} color={G.purple} icon="🆕" />
            <StatCard label="Total Questions" value={data.topQuestions.length > 0 ? "Live" : "0"} sub="across all topics" color={G.warn} icon="❓" />
            <StatCard label="Total Listings" value={data.topListings.length} sub={`${data.topListings.filter(l=>l.is_verified).length} verified`} color="#4ECDC4" icon="🏢" />
          </div>

          {/* Activity Chart */}
          <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, padding:24, marginBottom:24 }}>
            <div style={{ fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:20 }}>Activity — Last 14 Days</div>
            <div style={{ display:"flex", alignItems:"flex-end", gap:6, height:120 }}>
              {last14days.map((d, i) => (
                <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:2, height:"100%" }}>
                  <div style={{ flex:1, width:"100%", display:"flex", flexDirection:"column", justifyContent:"flex-end", gap:2 }}>
                    <div style={{ background:G.purple+"88", borderRadius:"2px 2px 0 0", height:`${(d.answers/maxBar)*100}%`, minHeight: d.answers>0?4:0, transition:"height 0.3s" }} title={`${d.answers} answers`} />
                    <div style={{ background:G.accent+"88", borderRadius:"2px 2px 0 0", height:`${(d.questions/maxBar)*100}%`, minHeight: d.questions>0?4:0, transition:"height 0.3s" }} title={`${d.questions} questions`} />
                  </div>
                  <div style={{ fontSize:9, color:"#444", fontFamily:G.mono, textAlign:"center", marginTop:4 }}>{d.shortDate}</div>
                </div>
              ))}
            </div>
            <div style={{ display:"flex", gap:16, marginTop:12 }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:G.muted }}>
                <div style={{ width:10, height:10, background:G.accent+"88", borderRadius:2 }} /> Questions
              </div>
              <div style={{ display:"flex", alignItems:"center", gap:6, fontSize:11, color:G.muted }}>
                <div style={{ width:10, height:10, background:G.purple+"88", borderRadius:2 }} /> Answers
              </div>
            </div>
          </div>

          {/* Recent Complaints */}
          {data.recentComplaints.length > 0 && (
            <div style={{ background:G.surface, border:`1px solid ${G.danger}44`, borderRadius:12, padding:24 }}>
              <div style={{ fontSize:11, color:G.danger, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase", marginBottom:16 }}>⚠ Recent Complaints</div>
              {data.recentComplaints.map((c, i) => (
                <div key={i} style={{ display:"flex", alignItems:"center", gap:12, padding:"10px 0", borderBottom:`1px solid ${G.border}`, flexWrap:"wrap" }}>
                  <span style={{ fontSize:11, color:G.danger, background:G.danger+"15", padding:"2px 8px", borderRadius:20, fontFamily:G.mono }}>{c.type}</span>
                  <Link to={`/listings/${c.listing_slug}`} style={{ fontSize:13, color:G.text, textDecoration:"none", fontWeight:500 }}>{c.listing_name}</Link>
                  {c.amount_lost && <span style={{ fontSize:12, color:G.danger, fontFamily:G.mono }}>-${Number(c.amount_lost).toLocaleString()}</span>}
                  <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono, marginLeft:"auto" }}>{c.author} · {new Date(c.created_at).toLocaleDateString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Questions Tab */}
      {tab === "questions" && (
        <div>
          <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, overflow:"hidden", marginBottom:24 }}>
            <div style={{ padding:"14px 20px", borderBottom:`1px solid ${G.border}`, fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase" }}>Top Questions by Views</div>
            {data.topQuestions.map((q, i) => (
              <div key={i} style={{ padding:"14px 20px", borderBottom:`1px solid #0F0F0F`, display:"flex", alignItems:"center", gap:16 }}>
                <span style={{ fontSize:16, fontWeight:700, color:"#2A2A2A", fontFamily:G.mono, width:24, textAlign:"center" }}>#{i+1}</span>
                <div style={{ flex:1 }}>
                  <Link to={`/q/${q.slug}`} style={{ textDecoration:"none" }}>
                    <div style={{ fontSize:14, color:G.text, fontWeight:500, marginBottom:4, lineHeight:1.4 }}>{q.title}</div>
                  </Link>
                  <div style={{ display:"flex", gap:12 }}>
                    <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>by {q.author}</span>
                    <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>💬 {q.answer_count} answers</span>
                    <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>{new Date(q.created_at).toLocaleDateString()}</span>
                  </div>
                </div>
                <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:120 }}>
                  <MiniBar value={q.views} max={maxViews} color={G.accent} />
                  <span style={{ fontSize:13, color:G.accent, fontFamily:G.mono, fontWeight:700, minWidth:40, textAlign:"right" }}>{q.views}</span>
                </div>
              </div>
            ))}
            {data.topQuestions.length === 0 && <div style={{ padding:"40px 0", textAlign:"center", color:"#333" }}>No questions yet</div>}
          </div>

          {/* Topics breakdown */}
          <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, overflow:"hidden" }}>
            <div style={{ padding:"14px 20px", borderBottom:`1px solid ${G.border}`, fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase" }}>Questions by Topic</div>
            {data.topTopics.map((t, i) => (
              <div key={i} style={{ padding:"12px 20px", borderBottom:`1px solid #0F0F0F`, display:"flex", alignItems:"center", gap:16 }}>
                <div style={{ width:10, height:10, borderRadius:"50%", background:t.color, flexShrink:0 }} />
                <span style={{ fontSize:13, color:G.text, flex:1 }}>{t.name}</span>
                <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:150 }}>
                  <MiniBar value={t.question_count} max={maxQCount} color={t.color} />
                  <span style={{ fontSize:13, color:G.text, fontFamily:G.mono, minWidth:30, textAlign:"right" }}>{t.question_count}</span>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Users Tab */}
      {tab === "users" && (
        <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, overflow:"hidden" }}>
          <div style={{ padding:"14px 20px", borderBottom:`1px solid ${G.border}`, fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase" }}>Most Active Users</div>
          {data.topUsers.map((u, i) => (
            <div key={i} style={{ padding:"14px 20px", borderBottom:`1px solid #0F0F0F`, display:"flex", alignItems:"center", gap:16 }}>
              <span style={{ fontSize:16, fontWeight:700, color:"#2A2A2A", fontFamily:G.mono, width:24, textAlign:"center" }}>#{i+1}</span>
              <Link to={`/u/${u.username}`} style={{ textDecoration:"none", flex:1 }}>
                <div style={{ fontSize:14, color:G.text, fontWeight:500 }}>{u.username}</div>
                <div style={{ display:"flex", gap:12, marginTop:2 }}>
                  <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>❓ {u.questions} questions</span>
                  <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>💬 {u.answers} answers</span>
                  <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>joined {new Date(u.created_at).toLocaleDateString()}</span>
                </div>
              </Link>
              <div style={{ display:"flex", alignItems:"center", gap:10, minWidth:120 }}>
                <MiniBar value={u.total_activity} max={maxActivity} color={G.purple} />
                <span style={{ fontSize:13, color:G.purple, fontFamily:G.mono, fontWeight:700, minWidth:30, textAlign:"right" }}>{u.total_activity}</span>
              </div>
            </div>
          ))}
          {data.topUsers.length === 0 && <div style={{ padding:"40px 0", textAlign:"center", color:"#333" }}>No users yet</div>}
        </div>
      )}

      {/* Listings Tab */}
      {tab === "listings" && (
        <div style={{ background:G.surface, border:`1px solid ${G.border}`, borderRadius:12, overflow:"hidden" }}>
          <div style={{ padding:"14px 20px", borderBottom:`1px solid ${G.border}`, fontSize:11, color:G.muted, fontFamily:G.mono, letterSpacing:1, textTransform:"uppercase" }}>Listings by Activity</div>
          {data.topListings.map((l, i) => (
            <div key={i} style={{ padding:"14px 20px", borderBottom:`1px solid #0F0F0F`, display:"flex", alignItems:"center", gap:16, flexWrap:"wrap" }}>
              <span style={{ fontSize:16, fontWeight:700, color:"#2A2A2A", fontFamily:G.mono, width:24, textAlign:"center" }}>#{i+1}</span>
              <div style={{ flex:1 }}>
                <Link to={`/listings/${l.slug}`} style={{ textDecoration:"none" }}>
                  <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:4 }}>
                    <span style={{ fontSize:14, color:G.text, fontWeight:500 }}>{l.name}</span>
                    {l.is_verified && <span style={{ fontSize:10, color:G.accent }}>✓</span>}
                    {l.is_flagged && <span style={{ fontSize:10, color:G.danger }}>⚠️</span>}
                  </div>
                </Link>
                <div style={{ display:"flex", gap:12 }}>
                  <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>{l.category}</span>
                  <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>⭐ {l.avg_rating||"N/A"}</span>
                  <span style={{ fontSize:11, color:G.muted, fontFamily:G.mono }}>💬 {l.review_count} reviews</span>
                  {l.complaint_count > 0 && <span style={{ fontSize:11, color:G.danger, fontFamily:G.mono }}>⚠ {l.complaint_count} complaints</span>}
                </div>
              </div>
            </div>
          ))}
          {data.topListings.length === 0 && <div style={{ padding:"40px 0", textAlign:"center", color:"#333" }}>No listings yet</div>}
        </div>
      )}
    </div>
  );
}
